import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Header from "@/components/landing/Header";
import PageFooter from "@/components/landing/PageFooter";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Billing fields are temporarily hidden from the UI, but the payload structure
// is preserved so the backend still receives valid data. A placeholder ("—")
// satisfies the current min_length=1 validation on the backend.
const initialForm = {
  fullName: "",
  email: "",
  phone: "",
  motivation: "",
  desiredChange: "",
  additionalNotes: "",
  billingType: "magan",
  billingName: "—",
  billingPostal: "—",
  billingCity: "—",
  billingAddress: "—",
  taxNumber: "",
  privacyAccepted: false,
  marketingAccepted: false,
};

export default function ExperienceRegistration() {
  const navigate = useNavigate();
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState("");

  function handleChange(e) {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === "checkbox" ? checked : value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: undefined }));
  }

  function validate() {
    const next = {};
    if (!form.fullName.trim()) next.fullName = "Kérlek, add meg a nevedet.";
    if (!form.email.trim()) next.email = "Az e-mail cím megadása kötelező.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      next.email = "Kérlek, adj meg egy érvényes e-mail címet.";
    if (!form.phone.trim()) next.phone = "Kérlek, add meg a telefonszámodat.";
    if (!form.motivation.trim())
      next.motivation = "Néhány mondat is elég — oszd meg, mi mozdult meg benned.";
    if (!form.privacyAccepted)
      next.privacyAccepted = "Az adatkezelési tájékoztató elfogadása szükséges.";
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setSubmitError("");
    if (!validate()) {
      const firstErrorEl = document.querySelector("[data-error='true']");
      if (firstErrorEl) firstErrorEl.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/experience-registration`, form);
      navigate("/elmenyprogram/koszonet");
    } catch (err) {
      setSubmitError(
        err?.response?.data?.detail ||
          "Sajnos most nem sikerült elküldeni a jelentkezésedet. Kérlek, próbáld újra néhány perc múlva."
      );
      setLoading(false);
    }
  }

  return (
    <main
      data-testid="experience-registration-page"
      className="bg-warm text-graphite min-h-screen"
    >
      <Header />
      <div className="pt-24 md:pt-28" />

      {/* Opening */}
      <section
        data-testid="registration-intro"
        className="px-6 md:px-10 py-10 md:py-14"
      >
        <div className="max-w-2xl mx-auto text-center">
          <h1
            data-testid="registration-title"
            className="font-serif-display text-[36px] sm:text-[44px] md:text-[52px] leading-[1.15] text-graphite tracking-tight mb-8"
          >
            Adj teret{" "}
            <em className="text-gold" style={{ fontStyle: "italic" }}>
              Önmagadnak
            </em>
          </h1>
          <div className="font-body font-light text-[16px] md:text-[17px] leading-[1.9] text-graphite space-y-5 text-center max-w-xl mx-auto">
            <p>Örülök, hogy megszólított ez a négy nap.</p>
            <p>
              A jelentkezés elküldése még nem automatikus fizetés. Az adatok beérkezése
              után személyesen felveszem veled a kapcsolatot, és e-mailben küldöm el a
              részvételhez szükséges következő lépéseket, valamint a díjbekérőt.
            </p>
            <p className="font-serif-display italic text-graphite-soft">
              Kérlek, szánj néhány nyugodt percet az űrlap kitöltésére.
            </p>
          </div>
        </div>
      </section>

      <form
        onSubmit={handleSubmit}
        data-testid="registration-form"
        noValidate
        className="max-w-2xl mx-auto px-6 md:px-10 pb-16"
      >
        {/* SECTION 1 — Contact details */}
        <FormSection title="Kapcsolattartási adatok">
          <Field
            id="fullName"
            label="Teljes név"
            required
            error={errors.fullName}
            value={form.fullName}
            onChange={handleChange}
            placeholder="Hogy szólíthatlak?"
          />
          <Field
            id="email"
            type="email"
            label="E-mail-cím"
            required
            error={errors.email}
            value={form.email}
            onChange={handleChange}
            placeholder="pelda@email.hu"
          />
          <Field
            id="phone"
            type="tel"
            label="Telefonszám"
            required
            error={errors.phone}
            value={form.phone}
            onChange={handleChange}
            placeholder="+36 30 123 4567"
          />
        </FormSection>

        {/* SECTION 2 — About you */}
        <FormSection title="Néhány gondolat rólad">
          <TextareaField
            id="motivation"
            label="Mi szólított meg leginkább ebben a négy napban?"
            required
            error={errors.motivation}
            value={form.motivation}
            onChange={handleChange}
            hint="Nincs jó vagy rossz válasz. Néhány mondat is elég arról, ami most megmozdult benned."
            rows={4}
          />
          <TextareaField
            id="desiredChange"
            label="Milyen változásnak vagy új minőségnek szeretnél most teret adni az életedben?"
            value={form.desiredChange}
            onChange={handleChange}
            hint="Ez a mező opcionális."
            rows={3}
          />
          <TextareaField
            id="additionalNotes"
            label="Van-e bármi, amit fontosnak érzel előre megosztani velem?"
            value={form.additionalNotes}
            onChange={handleChange}
            hint="Itt jelezhetsz például olyan élethelyzetet, érzékenységet vagy körülményt, amelyről jó, ha tudok a program előtt."
            rows={3}
          />
        </FormSection>

        {/* SECTION 3 — Billing (temporarily hidden) */}

        {/* SECTION 4 — Consents */}
        <FormSection title="Hozzájárulások">
          <div>
            <label
              className="flex items-start gap-3 cursor-pointer group"
              data-error={errors.privacyAccepted ? "true" : "false"}
            >
              <input
                type="checkbox"
                name="privacyAccepted"
                checked={form.privacyAccepted}
                onChange={handleChange}
                data-testid="registration-privacy-accepted"
                className="mt-1.5 accent-gold"
              />
              <span className="font-body font-light text-[14px] md:text-[15px] text-graphite leading-[1.75]">
                Elolvastam és elfogadom az{" "}
                <a
                  href="/adatkezelesi-tajekoztato"
                  target="_blank"
                  rel="noreferrer"
                  className="text-gold border-b border-gold hover:opacity-80"
                >
                  adatkezelési tájékoztatót
                </a>
                , és hozzájárulok ahhoz, hogy a megadott adataimat a jelentkezés
                feldolgozásához és a programmal kapcsolatos kapcsolattartáshoz
                felhasználják.
                <span className="text-gold ml-1">*</span>
              </span>
            </label>
            {errors.privacyAccepted && <ErrorText>{errors.privacyAccepted}</ErrorText>}
          </div>

          <label className="flex items-start gap-3 cursor-pointer group">
            <input
              type="checkbox"
              name="marketingAccepted"
              checked={form.marketingAccepted}
              onChange={handleChange}
              data-testid="registration-marketing-accepted"
              className="mt-1.5 accent-gold"
            />
            <span className="font-body font-light text-[14px] md:text-[15px] text-graphite leading-[1.75]">
              Szeretnék a jövőben értesülni Szász-Tóth Enikő programjairól, írásairól és
              új lehetőségeiről.
            </span>
          </label>
        </FormSection>

        {/* SUMMARY BEFORE SUBMIT */}
        <aside
          data-testid="registration-summary"
          className="my-12 mx-auto max-w-2xl bg-tinted border border-hairline px-7 md:px-10 py-8 md:py-10 text-center"
        >
          <p className="font-serif-display italic text-graphite text-[19px] md:text-[22px] leading-[1.5] mb-6">
            A jelentkezés{" "}
            <em className="text-gold" style={{ fontStyle: "italic" }}>
              következő lépése
            </em>
          </p>
          <div className="font-body font-light text-[14.5px] md:text-[15.5px] leading-[1.85] text-graphite space-y-3 max-w-lg mx-auto">
            <p>
              A jelentkezésed elküldése után személyesen felveszem veled a kapcsolatot.
            </p>
            <p>
              Amint a csoport összeállt, e-mailben elküldöm a részvételi díj
              befizetéséhez szükséges információkat és a díjbekérőt.
            </p>
            <p>A helyed a részvételi díj beérkezésével válik véglegessé.</p>
          </div>
          <div className="mt-6 pt-5 border-t border-hairline grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-md mx-auto">
            <div className="text-center">
              <p className="font-body text-[10px] tracking-[0.28em] uppercase text-gold mb-1">
                Early Bird
              </p>
              <p className="font-serif-display text-graphite text-[22px] md:text-[26px] leading-none">
                <span className="text-gold">99&thinsp;000&nbsp;Ft</span>
              </p>
              <p className="font-body text-[12px] text-muted-warm italic font-light mt-1">
                2026. augusztus 1.
              </p>
            </div>
            <div className="text-center">
              <p className="font-body text-[10px] tracking-[0.28em] uppercase text-muted-warm mb-1">
                Teljes részvételi díj
              </p>
              <p className="font-serif-display text-graphite text-[22px] md:text-[26px] leading-none">
                198&thinsp;000&nbsp;Ft
              </p>
            </div>
          </div>
        </aside>

        {submitError && (
          <p
            data-testid="registration-submit-error"
            className="text-center text-[14px] font-body text-red-700 mb-4"
          >
            {submitError}
          </p>
        )}

        <div className="text-center">
          <button
            type="submit"
            disabled={loading}
            data-testid="registration-submit"
            className="cta-gold-outline inline-flex items-center gap-3 px-9 py-4 text-[12.5px] uppercase font-body font-medium disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {loading ? "Elküldés folyamatban…" : "Elküldöm a jelentkezésemet"}
            {!loading && <span aria-hidden="true">→</span>}
          </button>
          <p className="mt-5 text-[12.5px] text-muted-warm font-body font-light italic">
            A jelentkezés elküldése nem jár azonnali fizetési kötelezettséggel.
          </p>
        </div>
      </form>

      <PageFooter />
    </main>
  );
}

/* --- Reusable subcomponents -------------------------------------------- */

function FormSection({ title, subtitle, children }) {
  const items = React.Children.toArray(children).filter(Boolean);
  return (
    <section className="mb-14 md:mb-16 pt-10 md:pt-12 border-t border-hairline first:border-t-0 first:pt-0">
      <h2 className="font-serif-display text-graphite text-[22px] md:text-[26px] leading-[1.3] mb-2">
        {title}
      </h2>
      {subtitle && (
        <p className="font-body font-light text-[13.5px] md:text-[14.5px] text-muted-warm italic leading-[1.7] mb-6">
          {subtitle}
        </p>
      )}
      {/* Each question separated by a subtle hairline for clear visual rhythm */}
      <div className="mt-8">
        {items.map((child, i) => (
          <div
            key={i}
            className={`py-7 md:py-8 ${
              i > 0 ? "border-t border-hairline" : ""
            }`}
          >
            {child}
          </div>
        ))}
      </div>
    </section>
  );
}

function FieldLabel({ htmlFor, required, children }) {
  return (
    <label
      htmlFor={htmlFor}
      className="block text-[11px] tracking-[0.3em] uppercase text-muted-warm mb-2 font-body"
    >
      {children}
      {required && <span className="text-gold ml-1">*</span>}
    </label>
  );
}

function ErrorText({ children }) {
  return (
    <p className="mt-2 text-[13px] font-body font-light text-red-700">{children}</p>
  );
}

function Field({ id, label, type = "text", required, error, hint, ...props }) {
  return (
    <div data-error={error ? "true" : "false"}>
      <FieldLabel htmlFor={id} required={required}>
        {label}
      </FieldLabel>
      <input
        id={id}
        name={id}
        type={type}
        data-testid={`registration-input-${id}`}
        className="input-underline"
        {...props}
      />
      {hint && !error && (
        <p className="mt-2 text-[12.5px] font-body font-light italic text-muted-warm leading-[1.55]">
          {hint}
        </p>
      )}
      {error && <ErrorText>{error}</ErrorText>}
    </div>
  );
}

function TextareaField({ id, label, required, error, hint, rows = 4, ...props }) {
  return (
    <div data-error={error ? "true" : "false"}>
      <FieldLabel htmlFor={id} required={required}>
        {label}
      </FieldLabel>
      <textarea
        id={id}
        name={id}
        rows={rows}
        data-testid={`registration-input-${id}`}
        className="input-underline resize-none"
        {...props}
      />
      {hint && !error && (
        <p className="mt-2 text-[12.5px] font-body font-light italic text-muted-warm leading-[1.55]">
          {hint}
        </p>
      )}
      {error && <ErrorText>{error}</ErrorText>}
    </div>
  );
}
